import React from 'react';
import PropTypes from 'prop-types';

/**
 * Inline Lucide-style SVG icons for the tender levelling view.
 *
 * Ported from the Claude Design handoff (icons.jsx). Self-contained SVG paths
 * so the view needs no icon-font dependency (the prototype used Material
 * Symbols); only the icons the levelling table actually uses are kept.
 */

const PATHS = {
  chevron_right: ['M9 18l6-6-6-6'],
  unfold_more: ['M7 15l5 5 5-5', 'M7 9l5-5 5 5'],
  expand_less: ['M18 15l-6-6-6 6'],
  trophy: [
    'M6 9H4.5a2.5 2.5 0 0 1 0-5H6',
    'M18 9h1.5a2.5 2.5 0 0 0 0-5H18',
    'M4 22h16',
    'M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22',
    'M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22',
    'M18 2H6v7a6 6 0 0 0 12 0V2Z',
  ],
  visibility: [
    'M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z',
    'circle:12,12,3',
  ],
  visibility_off: [
    'M9.88 9.88a3 3 0 1 0 4.24 4.24',
    'M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68',
    'M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61',
    'M2 2l20 20',
  ],
  remove: ['M5 12h14'],
  add: ['M5 12h14', 'M12 5v14'],
  subdirectory_arrow_right: ['M15 10l5 5-5 5', 'M4 4v7a4 4 0 0 0 4 4h12'],
  block: ['circle:12,12,10', 'M4.9 4.9l14.2 14.2'],
  flag: [
    'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z',
    'M4 22v-7',
  ],
  expand: ['M18 8l4 4-4 4', 'M6 8l-4 4 4 4', 'M2 12h20'],
  receipt_long: [
    'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z',
    'M14 8H8',
    'M16 12H8',
    'M13 16H8',
  ],
  sticky_note_2: [
    'M15.5 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h11a2 2 0 0 0 2-2V8.5L15.5 3Z',
    'M15 3v6h6',
  ],
  splitscreen: ['rect:18,18,3,3,2', 'M12 3v18'],
  payments: ['rect:20,12,2,6,2', 'circle:12,12,2', 'M6 12h.01', 'M18 12h.01'],
  trending_up: ['M22 7l-8.5 8.5-5-5L2 17', 'M16 7h6v6'],
};

function part(spec, i) {
  if (typeof spec === 'string' && spec.indexOf('circle:') === 0) {
    const [cx, cy, r] = spec.slice(7).split(',').map(Number);
    return <circle key={i} cx={cx} cy={cy} r={r} />;
  }
  if (typeof spec === 'string' && spec.indexOf('rect:') === 0) {
    const [w, h, x, y, rx] = spec.slice(5).split(',').map(Number);
    return <rect key={i} width={w} height={h} x={x} y={y} rx={rx} />;
  }
  return <path key={i} d={spec} />;
}

const LevellingIcon = ({ name, size = 20, style, className, strokeWidth }) => {
  const specs = PATHS[name];
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth || 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        display: 'inline-block',
        flex: '0 0 auto',
        verticalAlign: 'middle',
        ...style,
      }}
    >
      {specs ? specs.map(part) : null}
    </svg>
  );
};

LevellingIcon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  style: PropTypes.object,
  className: PropTypes.string,
  strokeWidth: PropTypes.number,
};

export default LevellingIcon;
